// Small helpers used by the editor's live preview.
//
// - renderWithSampleContext: light frontend-only mustache substitution so
//   the preview reflects what the recipient will see. The server-side
//   renderer (EmailTemplateVariableResolver) is still the source of
//   truth at send-time; this is a display-only reprise so the editor
//   feels immediate.
// - smsSegments: GSM-7 vs Unicode split + per-segment character math
//   matching the industry-standard SMS encoding rules INTALK uses.

export function renderWithSampleContext(
  input: string,
  ctx: Record<string, string>,
): string {
  if (!input) return "";
  return input.replace(/\{\{\s*([a-zA-Z0-9_\.]+)\s*\}\}/g, (_, key) => {
    return ctx[key] ?? `{{${key}}}`;
  });
}

const GSM7_BASIC =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ ÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const GSM7_EXTENDED = "^{}\\[~]|€";

function isGsm7(body: string): boolean {
  for (const ch of body) {
    if (GSM7_BASIC.includes(ch)) continue;
    if (GSM7_EXTENDED.includes(ch)) continue;
    return false;
  }
  return true;
}

// GSM-7 extended characters count as 2. Unicode characters count as 1 UTF-16
// code unit but the segment size drops to 70 / 67.
export function smsSegments(body: string): {
  chars: number;
  segments: number;
  perSegment: number;
  encoding: "gsm7" | "unicode";
} {
  if (!body) return { chars: 0, segments: 1, perSegment: 160, encoding: "gsm7" };

  if (isGsm7(body)) {
    let chars = 0;
    for (const ch of body) chars += GSM7_EXTENDED.includes(ch) ? 2 : 1;
    const perSegment = chars <= 160 ? 160 : 153;
    const segments = Math.max(1, Math.ceil(chars / perSegment));
    return { chars, segments, perSegment, encoding: "gsm7" };
  }

  const chars = Array.from(body).length;
  const perSegment = chars <= 70 ? 70 : 67;
  const segments = Math.max(1, Math.ceil(chars / perSegment));
  return { chars, segments, perSegment, encoding: "unicode" };
}
